import {
  useResultTemplatesQuery,
  useCreateBroadsheetTemplateMutation,
} from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { useFormErrors } from '@/composables/useFormErrors';
import {
  DEFAULT_STUDENT_INFO_KEYS,
  DEFAULT_TERTIARY_STUDENT_INFO_KEYS,
  STUDENT_INFO_OPTIONS,
  buildBroadsheetColumnDrafts,
  buildBroadsheetPreviewRows,
  buildCreateBroadsheetInput,
  collectSourceKeyOptions,
  partialCoverageSelections,
  type BroadsheetPositionChoice,
  type BroadsheetSourceKeyOption,
  type BroadsheetWizardAnswers,
} from '@/utils/broadsheetWizard';

export type BroadsheetWizardStepId =
  | 'name'
  | 'scores'
  | 'studentInfo'
  | 'percentage'
  | 'position'
  | 'headers'
  | 'displayOptions'
  | 'review';

interface TranscriptEntry {
  stepId: BroadsheetWizardStepId;
  question: string;
  answer: string;
}

export function useBroadsheetWizard(
  emit: (event: 'close' | 'saved') => void,
  isTertiary: { value: boolean },
) {
  const toast = useToast();
  const { serverErrors, handleServerErrors, clearErrors } = useFormErrors();

  // Queries — result templates feed the source-key chips
  const { result: templatesResult, loading: loadingTemplates } = useResultTemplatesQuery();
  const resultTemplates = computed(() => templatesResult.value?.resultTemplates?.data || []);
  const sourceKeyOptions = computed<BroadsheetSourceKeyOption[]>(
    () => collectSourceKeyOptions(resultTemplates.value),
  );

  // Mutation
  const { mutate: createBroadsheetTemplate } = useCreateBroadsheetTemplateMutation();

  const answers = reactive<BroadsheetWizardAnswers>({
    name: '',
    sourceKeys: [],
    studentInfoKeys: [],
    includePercentage: true,
    positionChoice: 'sum',
    useCourseAbbreviation: false,
    showUnitInHeaders: true,
    showStatusInHeaders: true,
    showCourseLegend: true,
    showGradeLegend: true,
    showSignatures: true,
    signatureTitles: [],
    pdfHeader: {
      schoolName: '',
      reportTitle: '',
      semesterFormat: 'number' as const,
      logoPosition: 'left' as const,
      levelDisplayMap: {},
    },
  });

  const transcript = ref<TranscriptEntry[]>([]);
  const saving = ref(false);

  // Per-step scratch state
  const nameInput = ref('');
  const selectedSourceKeys = ref<string[]>(['total']);
  const customSourceKeyInput = ref('');
  const customSourceKeys = ref<BroadsheetSourceKeyOption[]>([]);
  const selectedInfoKeys = ref<string[]>([]);

  const studentInfoOptions = computed(
    () => STUDENT_INFO_OPTIONS.filter((o) => !o.tertiaryOnly || isTertiary.value),
  );

  // Seed defaults once we know the school type.
  watch(isTertiary, (tertiary) => {
    if (selectedInfoKeys.value.length === 0) {
      selectedInfoKeys.value = [...(tertiary ? DEFAULT_TERTIARY_STUDENT_INFO_KEYS : DEFAULT_STUDENT_INFO_KEYS)];
    }
  }, { immediate: true });

  // --- Step machine ---------------------------------------------------------

  const stepSequence = computed<BroadsheetWizardStepId[]>(
    () => ['name', 'scores', 'studentInfo', 'percentage', 'position', 'headers', 'displayOptions', 'review'],
  );

  const answeredSteps = computed(() => new Set(transcript.value.map((t) => t.stepId)));

  const currentStep = computed<BroadsheetWizardStepId>(() => {
    const next = stepSequence.value.find((step) => !answeredSteps.value.has(step));
    return next || 'review';
  });

  const progress = computed(() => ({
    answered: transcript.value.length,
    total: stepSequence.value.length - 1,
  }));

  const subjectWord = computed(() => (isTertiary.value ? 'course' : 'subject'));

  const currentQuestion = computed<string>(() => {
    switch (currentStep.value) {
    case 'name':
      return `Hi! 👋 Let's build a broadsheet together — the class-wide grid that shows every student's ${subjectWord.value} scores in one table. What should we call it?`;
    case 'scores':
      return `Which score should appear under each ${subjectWord.value}? These come straight from your result templates — pick more than one and they'll show as grouped columns.`;
    case 'studentInfo':
      return 'Which student details should each row start with?';
    case 'percentage':
      return 'Should I add a Percentage column with each student\'s overall average? '
        + `I'll treat each ${subjectWord.value} as scored out of 100 — the standard setup. `
        + '(Different maximum? Use the advanced builder.)';
    case 'position':
      return 'Want to rank students with a Position column?';
    case 'headers':
      return `Should ${subjectWord.value} headers show the full name or the short code?`;
    case 'displayOptions':
      return 'Almost done! Which extra details should the broadsheet include? Toggle the ones you want — all are on by default.';
    case 'review':
      return 'That\'s everything! Check the sample broadsheet, then create it when you\'re happy.';
    default:
      return '';
    }
  });

  function pushAnswer(stepId: BroadsheetWizardStepId, answer: string) {
    transcript.value.push({ stepId, question: currentQuestion.value, answer });
  }

  function goBack() {
    if (transcript.value.length === 0 || saving.value) return;
    const last = transcript.value.pop();
    if (last?.stepId === 'name') nameInput.value = answers.name;
    clearErrors();
  }

  function jumpBackTo(index: number) {
    if (saving.value) return;
    const target = transcript.value[index];
    if (!target) return;
    transcript.value.splice(index);
    if (target.stepId === 'name') nameInput.value = answers.name;
    clearErrors();
  }

  // --- Step answer handlers --------------------------------------------------

  function answerName() {
    const name = nameInput.value.trim();
    if (!name) return;
    answers.name = name;
    pushAnswer('name', `"${name}"`);
  }

  const allSourceKeyOptions = computed<BroadsheetSourceKeyOption[]>(() => {
    const fromTemplates = sourceKeyOptions.value;
    const extras = customSourceKeys.value.filter(
      (custom) => !fromTemplates.some((o) => o.key === custom.key),
    );
    if (fromTemplates.length === 0 && extras.length === 0) {
      // School has no result templates yet — offer the universal default.
      return [{ key: 'total', label: 'Total', templateNames: [], kind: 'score' as const }];
    }
    return [...fromTemplates, ...extras];
  });

  const showAllSourceKeys = ref(false);

  /**
   * First-time users see plain score keys (assessments, totals); derived
   * keys (grades, GPA plumbing) stay behind a "more options" toggle. Any
   * already-selected derived key stays visible so state is never hidden.
   */
  const visibleSourceKeyOptions = computed<BroadsheetSourceKeyOption[]>(() => {
    if (showAllSourceKeys.value) return allSourceKeyOptions.value;
    return allSourceKeyOptions.value.filter(
      (o) => o.kind === 'score' || selectedSourceKeys.value.includes(o.key),
    );
  });

  const hiddenSourceKeyCount = computed(
    () => allSourceKeyOptions.value.length - visibleSourceKeyOptions.value.length,
  );

  /**
   * Keys the user picked that only exist in SOME result templates —
   * classes on the other templates would show blank columns, so the
   * scores step warns about each one as it is selected.
   */
  const coverageWarnings = computed(() => partialCoverageSelections(
    selectedSourceKeys.value,
    allSourceKeyOptions.value,
    resultTemplates.value.length,
  ));

  function toggleSourceKey(key: string) {
    const selected = selectedSourceKeys.value;
    const index = selected.indexOf(key);
    if (index >= 0) selected.splice(index, 1);
    else selected.push(key);
  }

  function addCustomSourceKey() {
    const key = customSourceKeyInput.value.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    if (!key) return;
    if (!allSourceKeyOptions.value.some((o) => o.key === key)) {
      customSourceKeys.value.push({ key, label: key, templateNames: [], kind: 'score' });
    }
    if (!selectedSourceKeys.value.includes(key)) selectedSourceKeys.value.push(key);
    customSourceKeyInput.value = '';
  }

  function confirmSourceKeys() {
    const picked = allSourceKeyOptions.value.filter(
      (o) => selectedSourceKeys.value.includes(o.key),
    );
    if (picked.length === 0) return;
    answers.sourceKeys = picked;
    pushAnswer('scores', picked.map((p) => p.label).join(', '));
  }

  function toggleInfoKey(key: string) {
    const selected = selectedInfoKeys.value;
    const index = selected.indexOf(key);
    if (index >= 0) selected.splice(index, 1);
    else selected.push(key);
  }

  function confirmStudentInfo() {
    // Preserve canonical option order regardless of click order.
    answers.studentInfoKeys = studentInfoOptions.value
      .filter((o) => selectedInfoKeys.value.includes(o.key))
      .map((o) => o.key);
    pushAnswer(
      'studentInfo',
      answers.studentInfoKeys.length > 0
        ? studentInfoOptions.value
          .filter((o) => answers.studentInfoKeys.includes(o.key))
          .map((o) => o.label)
          .join(', ')
        : 'No student detail columns',
    );
  }

  function answerPercentage(include: boolean) {
    answers.includePercentage = include;
    pushAnswer('percentage', include ? 'Yes, show the percentage' : 'No percentage column');
  }

  function answerPosition(choice: BroadsheetPositionChoice) {
    answers.positionChoice = choice;
    const summary = choice === 'sum'
      ? 'Rank by total score'
      : choice === 'average' ? 'Rank by average score' : 'No ranking';
    pushAnswer('position', summary);
  }

  function answerHeaders(useAbbreviation: boolean) {
    answers.useCourseAbbreviation = useAbbreviation;
    pushAnswer('headers', useAbbreviation ? 'Short codes' : 'Full names');
  }

  // Display options scratch state
  const displayOpts = reactive({
    showUnitInHeaders: true,
    showStatusInHeaders: true,
    showCourseLegend: true,
    showGradeLegend: true,
    showSignatures: true,
    signatureInput: '',
    signatureTitles: [] as string[],
    pdfSchoolName: '',
    pdfReportTitle: '',
    pdfSemesterFormat: 'number' as 'full' | 'number',
    pdfLogoPosition: 'left' as 'left' | 'right',
    pdfLevelMapInput: '',
    pdfLevelDisplayMap: {} as Record<string, string>,
  });

  watch(isTertiary, (tertiary) => {
    if (displayOpts.signatureTitles.length === 0) {
      displayOpts.signatureTitles = tertiary
        ? ['Head of Department', 'Dean', 'External Examiner']
        : ['Class Teacher', 'Head Teacher'];
    }
  }, { immediate: true });

  function addSignatureTitle() {
    const title = displayOpts.signatureInput.trim();
    if (title && !displayOpts.signatureTitles.includes(title)) {
      displayOpts.signatureTitles.push(title);
    }
    displayOpts.signatureInput = '';
  }

  function removeSignatureTitle(index: number) {
    displayOpts.signatureTitles.splice(index, 1);
  }

  function addLevelMapping() {
    const raw = (displayOpts.pdfLevelMapInput || '').trim();
    const [level, display] = raw.split(':').map((s) => s.trim());
    if (level && display) {
      displayOpts.pdfLevelDisplayMap[level] = display;
      displayOpts.pdfLevelMapInput = '';
    }
  }

  function confirmDisplayOptions() {
    answers.showUnitInHeaders = displayOpts.showUnitInHeaders;
    answers.showStatusInHeaders = displayOpts.showStatusInHeaders;
    answers.showCourseLegend = displayOpts.showCourseLegend;
    answers.showGradeLegend = displayOpts.showGradeLegend;
    answers.showSignatures = displayOpts.showSignatures;
    answers.signatureTitles = [...displayOpts.signatureTitles];
    answers.pdfHeader = {
      schoolName: displayOpts.pdfSchoolName,
      reportTitle: displayOpts.pdfReportTitle,
      semesterFormat: displayOpts.pdfSemesterFormat,
      logoPosition: displayOpts.pdfLogoPosition,
      levelDisplayMap: { ...displayOpts.pdfLevelDisplayMap },
    };
    const parts: string[] = [];
    if (displayOpts.showUnitInHeaders) parts.push('Credit units');
    if (displayOpts.showStatusInHeaders) parts.push('E/C status');
    if (displayOpts.showCourseLegend) parts.push('Course legend');
    if (displayOpts.showSignatures) parts.push('Signatures');
    pushAnswer('displayOptions', parts.length > 0 ? parts.join(', ') : 'Defaults');
  }

  // --- Preview ---------------------------------------------------------------

  /** Like the result-template wizard, only answered steps appear in the preview. */
  const previewAnswers = computed<BroadsheetWizardAnswers>(() => {
    const done = answeredSteps.value;
    return {
      ...answers,
      sourceKeys: done.has('scores') ? answers.sourceKeys : [],
      studentInfoKeys: done.has('studentInfo') ? answers.studentInfoKeys : [],
      includePercentage: done.has('percentage') ? answers.includePercentage : false,
      positionChoice: done.has('position') ? answers.positionChoice : 'none',
    };
  });

  const previewRows = computed(() => buildBroadsheetPreviewRows(previewAnswers.value));
  const reviewColumns = computed(() => buildBroadsheetColumnDrafts(answers, isTertiary.value));

  // --- Save -------------------------------------------------------------------

  async function saveTemplate() {
    if (saving.value) return;
    saving.value = true;
    clearErrors();
    try {
      const input = buildCreateBroadsheetInput(answers, isTertiary.value);
      await createBroadsheetTemplate({ input });
      toast.add({
        severity: 'success',
        summary: 'Broadsheet template created',
        detail: `"${answers.name}" is ready — click its ▶ Generate Report button in the list to run it for a class.`,
        life: 4000,
      });
      emit('saved');
    } catch (e) {
      handleServerErrors(e);
      const error = e as Error;
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to create broadsheet template',
        life: 4000,
      });
    } finally {
      saving.value = false;
    }
  }

  return {
    // state
    answers,
    transcript,
    saving,
    nameInput,
    selectedSourceKeys,
    customSourceKeyInput,
    selectedInfoKeys,

    // machine
    currentStep,
    currentQuestion,
    progress,

    // data
    loadingTemplates,
    resultTemplates,
    allSourceKeyOptions,
    visibleSourceKeyOptions,
    hiddenSourceKeyCount,
    showAllSourceKeys,
    coverageWarnings,
    studentInfoOptions,
    subjectWord,
    serverErrors,

    // preview / review
    previewAnswers,
    previewRows,
    reviewColumns,

    // display options
    displayOpts,
    addSignatureTitle,
    removeSignatureTitle,
    addLevelMapping,

    // handlers
    answerName,
    toggleSourceKey,
    addCustomSourceKey,
    confirmSourceKeys,
    toggleInfoKey,
    confirmStudentInfo,
    answerPercentage,
    answerPosition,
    answerHeaders,
    confirmDisplayOptions,
    goBack,
    jumpBackTo,
    saveTemplate,
  };
}
